<template>
  <a-dropdown
    v-model:open="open"
    trigger="click"
    placement="bottomRight"
    :overlay-style="{ zIndex: 1100 }"
  >
    <button
      type="button"
      class="yt-more-btn"
      :aria-label="$t('header.more')"
      :title="$t('header.more')"
      @click.prevent
    >
      <i class="fa-solid fa-ellipsis-vertical" aria-hidden="true" />
    </button>

    <template #overlay>
      <div class="yt-guest-menu" role="menu" @click.stop>
        <!-- Main -->
        <template v-if="panel === 'main'">
          <button type="button" class="yt-guest-menu__item" @click="goPrivacy">
            <i class="fa-solid fa-user-shield" aria-hidden="true" />
            <span>{{ $t('header.yourData') }}</span>
          </button>

          <button type="button" class="yt-guest-menu__item" @click="panel = 'language'">
            <i class="fa-solid fa-language" aria-hidden="true" />
            <span>{{ $t('header.displayLanguage', { lang: langLabel }) }}</span>
            <i class="fa-solid fa-chevron-right yt-guest-menu__chevron" aria-hidden="true" />
          </button>

          <button type="button" class="yt-guest-menu__item" @click="panel = 'location'">
            <i class="fa-solid fa-globe" aria-hidden="true" />
            <span>{{ $t('header.locationLabel', { place: locationLabel }) }}</span>
            <i class="fa-solid fa-chevron-right yt-guest-menu__chevron" aria-hidden="true" />
          </button>

          <div class="yt-guest-menu__divider" />

          <button type="button" class="yt-guest-menu__item" @click="goSettings">
            <i class="fa-solid fa-gear" aria-hidden="true" />
            <span>{{ $t('header.settings') }}</span>
          </button>

          <div class="yt-guest-menu__divider" />

          <button type="button" class="yt-guest-menu__item" @click="goHelp">
            <i class="fa-regular fa-circle-question" aria-hidden="true" />
            <span>{{ $t('header.help') }}</span>
          </button>
          <button type="button" class="yt-guest-menu__item" @click="goFeedback">
            <i class="fa-regular fa-message" aria-hidden="true" />
            <span>{{ $t('header.sendFeedback') }}</span>
          </button>
        </template>

        <!-- Language -->
        <template v-else-if="panel === 'language'">
          <button type="button" class="yt-guest-menu__back" @click="panel = 'main'">
            <i class="fa-solid fa-arrow-left" aria-hidden="true" />
            <span>{{ $t('header.language') }}</span>
          </button>
          <div class="yt-guest-menu__divider" />
          <button
            type="button"
            class="yt-guest-menu__item"
            :class="{ 'is-active': langCode === 'vn' }"
            @click="pickLang('vn')"
          >
            <span>Tiếng Việt</span>
            <i v-if="langCode === 'vn'" class="fa-solid fa-check" aria-hidden="true" />
          </button>
          <button
            type="button"
            class="yt-guest-menu__item"
            :class="{ 'is-active': langCode === 'us' }"
            @click="pickLang('us')"
          >
            <span>English</span>
            <i v-if="langCode === 'us'" class="fa-solid fa-check" aria-hidden="true" />
          </button>
        </template>

        <!-- Location -->
        <template v-else>
          <button type="button" class="yt-guest-menu__back" @click="panel = 'main'">
            <i class="fa-solid fa-arrow-left" aria-hidden="true" />
            <span>{{ $t('header.locationTitle') }}</span>
          </button>
          <div class="yt-guest-menu__divider" />
          <button
            type="button"
            class="yt-guest-menu__item is-active"
            @click="close"
          >
            <span>{{ locationLabel }}</span>
            <i class="fa-solid fa-check" aria-hidden="true" />
          </button>
        </template>
      </div>
    </template>
  </a-dropdown>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import router from '@/router'
import { CONTACT, FAQ_PATH, RULES } from '@/const/path'

const props = withDefaults(
  defineProps<{
    langCode?: string
    locationLabel?: string
  }>(),
  {
    langCode: 'vn',
    locationLabel: 'Vietnam',
  }
)

const emit = defineEmits<{
  (e: 'change-lang', lang: 'vn' | 'us'): void
}>()

const open = ref(false)
const panel = ref<'main' | 'language' | 'location'>('main')

const langLabel = computed(() =>
  props.langCode === 'us' ? 'English' : 'Tiếng Việt'
)

watch(open, (val) => {
  if (!val) panel.value = 'main'
})

const close = () => {
  open.value = false
  panel.value = 'main'
}

const pickLang = (lang: 'vn' | 'us') => {
  emit('change-lang', lang)
  panel.value = 'main'
}

const goPrivacy = () => {
  close()
  router.push({ path: RULES }).catch(() => undefined)
}

const goSettings = () => {
  close()
  router.push({ path: CONTACT }).catch(() => undefined)
}

const goHelp = () => {
  close()
  router.push({ path: FAQ_PATH }).catch(() => undefined)
}

const goFeedback = () => {
  close()
  router.push({ path: CONTACT }).catch(() => undefined)
}
</script>

<style scoped>
.yt-more-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: #0f0f0f;
  font-size: 16px;
  cursor: pointer;
}

.yt-more-btn:hover {
  background: #f2f2f2;
}

.yt-guest-menu {
  min-width: 300px;
  max-width: min(360px, 92vw);
  padding: 8px 0;
  background: #212121;
  border-radius: 12px;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.35);
  color: #fff;
  overflow: hidden;
}

.yt-guest-menu__item,
.yt-guest-menu__back {
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  min-height: 44px;
  padding: 0 16px;
  border: none;
  background: transparent;
  color: #fff;
  font-size: 14px;
  font-weight: 400;
  text-align: left;
  cursor: pointer;
}

.yt-guest-menu__item > i:first-child,
.yt-guest-menu__back > i:first-child {
  width: 20px;
  text-align: center;
  font-size: 16px;
  color: #fff;
  flex: 0 0 auto;
}

.yt-guest-menu__item > span {
  flex: 1;
  min-width: 0;
}

.yt-guest-menu__chevron {
  margin-left: auto;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.55);
}

.yt-guest-menu__item:hover,
.yt-guest-menu__back:hover {
  background: rgba(255, 255, 255, 0.1);
}

.yt-guest-menu__item.is-active {
  background: rgba(255, 255, 255, 0.08);
}

.yt-guest-menu__item.is-active > .fa-check {
  margin-left: auto;
  color: #3ea6ff;
}

.yt-guest-menu__divider {
  height: 1px;
  margin: 8px 0;
  background: rgba(255, 255, 255, 0.12);
}

.yt-guest-menu__back {
  font-weight: 500;
}
</style>
